/**
 * ที่มาของค่าจ้างฐานต่อคน สำหรับหน้าเงินเดือน — ให้ HR ตรวจทาน "เรท × วัน" กับวันสแกนจริง.
 *
 * pay_basis = ค่าที่ตัวรันเงินเดือนจำไว้ตอนคิดจริง (calculation_log). แถวที่รันก่อนมีช่องนี้
 * ถอยไปใช้สรุปเวลาปัจจุบัน + เรทในข้อมูลพนักงาน (ป้าย estimated บอกว่าอาจไม่ใช่ค่าตอนรัน).
 */

export type EmpType = 'monthly' | 'daily' | 'hourly';

export interface PayBasis {
  employee_type: EmpType;
  pay_only_attended: boolean;
  /** fb#484 — พาร์ทไทม์ (on_call): วันนักขัตฤกษ์ที่ไม่ได้มาไม่ได้ค่าจ้าง */
  public_holiday_unpaid?: boolean | null;
  /** นักขัตฤกษ์ที่มาทำ (มีสแกนเข้า) — พาร์ทไทม์ได้แรงฐานเฉพาะวันพวกนี้ */
  public_holiday_worked?: number | null;
  daily_rate: number | null;
  hourly_rate: number | null;
  working_days: number;
  paid_leave_days: number;
  holiday_days: number;
  /** วันหยุดประจำสัปดาห์ (ส่วนย่อยของ holiday_days) — รายวันไม่ได้ค่าจ้าง · ร่างที่รันก่อน fb#57 ไม่มีช่องนี้ */
  dayoff_days?: number | null;
  hours_worked: number | null;
  /** รายชั่วโมง: ชั่วโมงมาจากไหน (manual = HR กรอก · scan = รวมจากสแกนเข้า-ออก · none = ไม่มี) */
  hours_source?: 'manual' | 'scan' | 'none' | null;
  scan_days_counted?: number | null;
  scan_days_flagged?: Array<{ date: string; reason: string }> | null;
  /** fb#129 — เวลาเข้า/ออกที่ใช้คิด + ชม. รายวัน (รายชั่วโมงที่ดึงจากสแกน) */
  scan_hours_by_day?: Array<{ date: string; in: string; out: string; hours: number; scan_in?: string; scan_out?: string; trimmed_min?: number; break_min?: number }> | null;
  /** true = นับเฉพาะชั่วโมงเต็ม (เข้าปัดขึ้น ออกปัดลง) */
  scan_whole_hours?: boolean | null;
  /** fb#135 — true = นับเฉพาะในช่วงกะ · นาทีนอกกะที่ไม่นับ (รวมงวด) */
  scan_within_shift?: boolean | null;
  scan_trimmed_minutes?: number | null;
  /** fb#500 — true = วันที่เกิน 6 ชม. หักพัก 1 ชม. · นาทีพักที่หัก (รวมงวด) */
  scan_break_deduct?: boolean | null;
  scan_break_minutes?: number | null;
  /** fb#439 — สวิตช์ดึงจากสแกนปิด: ชั่วโมงที่สแกนได้แต่ยังไม่ใช้คิด (null = ใช้คิดแล้ว/กรอกเอง/ร่างเก่า) */
  scan_hours_available?: number | null;
  /** 'wht_pnd3' = ไม่หักประกันสังคม · หักภาษี ณ ที่จ่ายอัตราคงที่ */
  /** 'no_deduction' = สิทธิ์พิเศษ ไม่หักอะไรเลย (fb#113) */
  statutory_mode?: 'standard' | 'wht_pnd3' | 'no_deduction' | null;
  wht_rate_pct?: number | null;
  /** เวลาที่สรุปเวลาชุดที่รันนี้ใช้ถูกสร้าง (ก่อนสิ้นงวด = มีวันคาดการณ์) */
  summary_computed_at?: string | null;
}

export interface DaySummary {
  working_days: number;
  paid_leave_days: number;
  holiday_days: number;
  /** วันหยุดประจำสัปดาห์ (ส่วนย่อยของ holiday_days) — สรุปก่อน migration fb#57 = 0/ไม่มี */
  dayoff_days?: number | null;
  /** รุ่นสรุป (2 = แยก dayoff_days แล้ว) — migration เติม dayoff_days=0 ให้แถวเก่า จึงต้องดูรุ่นแทน */
  detail_version?: number | null;
  absent_days: number;
  unpaid_leave_days: number;
  computed_at: string | null;
}

export interface PayBasisRow {
  employee_type: EmpType | null;
  daily_rate: number | null;
  base_pay: number;
  pay_basis?: PayBasis | null;
  summary?: DaySummary | null;
  /** วันที่สแกนเข้างานในวันทำงาน (ไม่รวมวันหยุด) */
  scan_days?: number;
  /** สแกนในวันหยุด — reconcile นับเป็นวันหยุด+OT ไม่เข้าวันมาทำงาน */
  offday_scan_days?: number;
  /** วันที่มีทั้งสแกนและใบลา (ลาเศษวัน) */
  scan_leave_days?: number;
  period_end?: string;
}

export interface PayBasisView {
  type: EmpType;
  typeLabel: string;
  /** เรทที่ใช้คิด เช่น "฿356/วัน" · รายเดือน "฿15,000/เดือน" · ไม่รู้ = null */
  rateLabel: string | null;
  /** วันที่มาทำงาน (ตามสรุปเวลา) — รายเดือนก็โชว์ไว้เทียบกับสแกน */
  workedDays: number | null;
  /** วันที่เอาไปคูณเรท (รายวันเท่านั้น) */
  paidDays: number | null;
  /** สูตรแบบคนอ่าน เช่น "356 × (มาทำงาน 26 + นักขัตฤกษ์ 1) = 9,612" */
  formula: string | null;
  /** รายวัน: วันหยุดประจำสัปดาห์ในงวดที่ไม่ได้ค่าจ้าง (ม.56) — โชว์ให้ HR เห็นว่าทำไมไม่เท่าจำนวนวันในงวด */
  unpaidDayoffDays: number;
  /** รายวัน: วันหยุดตามประเพณี (นักขัตฤกษ์) ที่ได้ค่าจ้าง = holiday_days − dayoff_days */
  publicHolidayDays: number;
  /** true = ร่าง/สรุปรุ่นก่อน fb#57 ยังแยกวันหยุดประจำสัปดาห์ออกจากนักขัตฤกษ์ไม่ได้ (ต้องสรุปเวลา + รันร่างใหม่) */
  legacyHoliday: boolean;
  /** รายวัน pay_only_attended = จ่ายเฉพาะวันมาทำงาน (ลา/นักขัตฤกษ์ไม่ได้ค่าจ้าง) */
  onlyAttended: boolean;
  /** fb#484 — พาร์ทไทม์: นักขัตฤกษ์ไม่ได้ค่าจ้าง (ลายังได้ตามปกติ) */
  holidayUnpaid: boolean;
  paidHolidayDays: number;
  scanDays: number;
  offdayScanDays: number;
  /** วันมาทำงานที่ใช้คิด ≠ วันสแกนจริง (อธิบายด้วยลาเศษวันไม่ได้) → ควรตรวจ */
  scanMismatch: boolean;
  /** true = ไม่มีค่าที่จำตอนรัน ใช้สรุปเวลาปัจจุบันแทน */
  estimated: boolean;
}

const TYPE_LABEL: Record<EmpType, string> = { monthly: 'รายเดือน', daily: 'รายวัน', hourly: 'รายชั่วโมง' };

export function empTypeLabel(t: EmpType | null | undefined): string {
  return TYPE_LABEL[t ?? 'monthly'];
}

const num = (n: number) => n.toLocaleString('th-TH', { maximumFractionDigits: 3 });
const baht = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

export function payBasisView(row: PayBasisRow): PayBasisView {
  const b = row.pay_basis ?? null;
  const type: EmpType = b?.employee_type ?? row.employee_type ?? 'monthly';
  const s = row.summary ?? null;
  const worked = b ? b.working_days : s ? s.working_days : null;
  const paidLeave = b ? b.paid_leave_days : s?.paid_leave_days ?? 0;
  const holiday = b ? b.holiday_days : s?.holiday_days ?? 0;
  // fb#57 — วันหยุดประจำสัปดาห์แยกจากนักขัตฤกษ์ · ร่าง/สรุปก่อนมีช่องนี้ = แยกไม่ได้ (legacy: สูตรเดิม จนสรุปเวลา+รันร่างใหม่)
  const dayoffRaw = b ? b.dayoff_days : s?.dayoff_days;
  // ร่าง: ไม่มีช่อง dayoff_days = รันก่อน fb#57 · สรุป: migration เติม 0 ให้แถวเก่า → ดู detail_version < 2 แทน
  const legacyHoliday = b ? b.dayoff_days == null : (s == null || dayoffRaw == null || Number(s.detail_version ?? 0) < 2);
  const dayoff = Number(dayoffRaw ?? 0);
  const publicHoliday = Math.max(0, holiday - dayoff);
  const onlyAttended = b?.pay_only_attended ?? false;
  const holidayUnpaid = b?.public_holiday_unpaid ?? false;
  const paidHoliday = holidayUnpaid ? Number(b?.public_holiday_worked ?? 0) : publicHoliday;
  const scanDays = row.scan_days ?? 0;
  // ไม่ตรงเมื่อ (1) นับมาทำงานมากกว่าวันที่สแกน = มีวันคาดการณ์/นับโดยไม่มีสแกน หรือ
  // (2) สแกนมากกว่าที่นับ เกินกว่าที่ลาเศษวันจะอธิบายได้ (วันลาครึ่งวันที่มีสแกน
  // reconcile เครดิตมาทำงานแค่เศษ ส่วนที่เหลือไปอยู่ในลา)
  // ข้อจำกัด: สแกนวันหยุดที่เพิ่มหลังสรุปเวลา ยังไม่อยู่ใน *_worked_dates → เตือน (ซึ่งควรสรุปใหม่อยู่แล้ว)
  // รายเดือน: จ่ายตามฐาน ไม่ผูกกับวัน → ไม่เตือน
  // เผื่อเฉพาะวันที่มีทั้งสแกนและใบลา (แต่ละวันเครดิตมาทำงานไม่ถึง 1) — ลาเต็มวันที่ไม่มีสแกนไม่นับ
  const leaveTolerance = row.scan_leave_days ?? 0;
  const scanMismatch = type !== 'monthly' && worked != null && (
    worked > scanDays + 1e-9 || scanDays - worked > leaveTolerance + 1e-9
  );

  let rateLabel: string | null = null;
  let paidDays: number | null = null;
  let formula: string | null = null;

  if (type === 'daily') {
    const rate = b?.daily_rate ?? row.daily_rate;
    if (rate != null) rateLabel = `฿${baht(rate)}/วัน`;
    if (worked != null) {
      const parts = [`มาทำงาน ${num(worked)}`];
      if (!onlyAttended && paidLeave) parts.push(`ลาได้ค่าจ้าง ${num(paidLeave)}`);
      // รุ่นเก่า: holiday ยังรวมวันหยุดประจำสัปดาห์ → เรียก "วันหยุด" ตามเดิม ไม่แอบอ้างว่าเป็นนักขัตฤกษ์
      if (!onlyAttended && paidHoliday) parts.push(`${legacyHoliday ? 'วันหยุด' : 'นักขัตฤกษ์'} ${num(paidHoliday)}`);
      paidDays = onlyAttended ? worked : worked + paidLeave + paidHoliday;
      if (rate != null) {
        formula = `${baht(rate)} × (${parts.join(' + ')}) = ${baht(row.base_pay)}`;
      }
    }
  } else if (type === 'hourly') {
    const rate = b?.hourly_rate ?? row.daily_rate;
    if (rate != null) rateLabel = `฿${baht(rate)}/ชม.`;
    if (rate != null && b?.hours_worked != null) {
      const src =
        b.hours_source === 'scan'
          ? ` (จากการสแกน ${num(b.scan_days_counted ?? 0)} วัน${b.scan_whole_hours ? ' · นับชั่วโมงเต็ม' : ''}${b.scan_within_shift ? ` · นับเฉพาะในช่วงกะ${b.scan_trimmed_minutes ? ` (ตัดเวลานอกกะ ${num(b.scan_trimmed_minutes)} นาที)` : ''}` : ''}${b.scan_break_deduct ? ` · เกิน 6 ชม. หักพัก 1 ชม.${b.scan_break_minutes ? ` (หักรวม ${num(b.scan_break_minutes / 60)} ชม.)` : ''}` : ''}${b.scan_days_flagged?.length ? ` · ไม่นับ ${b.scan_days_flagged.length} วันที่สแกนไม่ครบคู่/ไม่ตรงกะ: ${b.scan_days_flagged.map((f) => f.date).join(', ')}` : ''})`
          : b.hours_source === 'manual' ? ' (กรอกเอง)'
          // fb#439 — สวิตช์ปิดแต่สแกนมี: บอกเลขที่สแกนได้ ไม่ใช่ "ยังไม่มีชั่วโมง" (ลูกค้าอ่านว่าระบบไม่เก็บเวลา)
          : b.hours_source === 'none' ? ((b.scan_hours_available ?? 0) > 0
            ? ` (สแกนได้ ${num(b.scan_hours_available ?? 0)} ชม. ${num(b.scan_days_counted ?? 0)} วัน แต่ตอนรันร่างสวิตช์ "ดึงชั่วโมงจากการสแกน" ปิด จึงยังไม่ใช้คิด)`
            : ' (ยังไม่มีชั่วโมง)') : '';
      formula = `${baht(rate)} × ${num(b.hours_worked)} ชม. = ${baht(row.base_pay)}${src}`;
    }
  }

  return {
    type,
    typeLabel: TYPE_LABEL[type],
    rateLabel,
    workedDays: worked,
    paidDays,
    formula,
    unpaidDayoffDays: type === 'daily' ? dayoff : 0,
    publicHolidayDays: type === 'daily' ? publicHoliday : 0,
    paidHolidayDays: type === 'daily' ? paidHoliday : 0,
    legacyHoliday: type === 'daily' && legacyHoliday,
    onlyAttended: type === 'daily' && onlyAttended,
    holidayUnpaid: type === 'daily' && holidayUnpaid,
    scanDays,
    offdayScanDays: row.offday_scan_days ?? 0,
    scanMismatch,
    estimated: b == null,
  };
}

/**
 * สรุปเวลาถูกทำก่อนสิ้นงวด → วันหลังจากนั้นระบบนับล่วงหน้าว่า "มาทำงาน" (ยังไม่มีสแกนให้ดู).
 * คืนวันที่สรุปเก่าสุด (YYYY-MM-DD เวลาไทย) ของคนที่เป็นแบบนี้ หรือ null ถ้าสรุปครบงวดแล้ว.
 */
export function staleSummaryDate(rows: PayBasisRow[]): { date: string; count: number } | null {
  let oldest: string | null = null;
  let count = 0;
  for (const r of rows) {
    // ยึดสรุปชุดที่ "รันเงินเดือนใช้จริง" — สรุปใหม่แล้วแต่ยังไม่รันร่างใหม่ ค่าจ้างยังเป็นวันคาดการณ์เดิม
    const at = r.pay_basis?.summary_computed_at ?? r.summary?.computed_at;
    if (!at || !r.period_end) continue;
    const d = new Date(at).toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
    if (d < r.period_end) {
      count++;
      if (!oldest || d < oldest) oldest = d;
    }
  }
  return oldest ? { date: oldest, count } : null;
}

/** สรุปเวลาถูกทำใหม่หลังรันเงินเดือน → ตัวเลขในตารางยังเป็นของชุดเก่า ต้องรันร่างใหม่ */
export function summaryNewerThanRun(rows: PayBasisRow[]): number {
  let n = 0;
  for (const r of rows) {
    const used = r.pay_basis?.summary_computed_at;
    const cur = r.summary?.computed_at;
    if (used && cur && new Date(cur).getTime() > new Date(used).getTime() + 1000) n++;
  }
  return n;
}
